import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

import { AiOutlineTable, AiOutlineCode } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import StorageIcon from "@mui/icons-material/Storage";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1515879218367-8466d910aaa4";

/* ✅ Styled Components - Premium Dark Glass */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.92), rgba(16,7,32,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "white",
    transition: "transform 0.3s ease",
    "&:hover": {
        border: "1px solid rgba(218, 165, 32, 0.3)",
    }
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ MAIN COMPONENT */
export default function PythonDataStructures() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const pythonModules = useMemo(() => [
        {
            title: "1. Lists (Ordered & Mutable)",
            code: `fruits = ["apple", "banana", "cherry"]\nfruits.append("orange")\nfruits[0] = "blueberry"\nprint(fruits) # ['blueberry', 'banana', 'cherry', 'orange']`,
            desc: "Lists are used to store multiple items in a single variable. They are ordered, changeable, and allow duplicate values. Use them for collections where order matters.",
            diagram: ""
        },
        {
            title: "2. Dictionaries (Key-Value Pairs)",
            code: `user = {\n    "name": "Hazzan",\n    "role": "Developer",\n    "level": 5\n}\nprint(user["name"]) # Access by key`,
            desc: "Dictionaries store data in 'key:value' pairs. They are ordered (as of Python 3.7) and changeable, but do not allow duplicate keys. Ideal for representing real-world objects.",
            diagram: ""
        },
        {
            title: "3. Tuples (Ordered & Immutable)",
            code: `coordinates = (10.0, 20.0)\n# coordinates[0] = 15.0  # This would raise a TypeError\n\npoint_a, point_b = coordinates # Unpacking`,
            desc: "Tuples are used to store multiple items in a single variable that cannot be changed once created (immutable). They are often used for data that shouldn't be modified, like GPS coordinates.",
            diagram: ""
        }
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Section Banner */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                        textTransform: "uppercase",
                        letterSpacing: "2px"
                    }}
                >
                    Python Data Structures
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Tutorial Introduction */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Organizing Information
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            Beyond simple variables, Python provides powerful built-in containers. Understanding when to use a List versus a Dictionary is the difference between writing "code" and building "software."
                        </Typography>

                        <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                • Indexed Collections
                            </Typography>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                • Key-Based Mapping
                            </Typography>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                • Memory Efficient Storage
                            </Typography>
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Learning Modules */}
                {pythonModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod", border: "1px solid goldenrod" }}>
                                        {i === 1 ? <StorageIcon size={22} /> : <AiOutlineTable size={24} />}
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)", "&:hover": { borderColor: "white" } }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{ color: "goldenrod", borderColor: "goldenrod", "&:hover": { bgcolor: "rgba(218, 165, 32, 0.1)" } }}
                                        >
                                            Test Code
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                                {item.desc}
                            </Typography>

                            <CodeContainer>
                                <Box sx={{ position: 'absolute', right: 10, top: 10, color: 'rgba(255,255,255,0.2)' }}>
                                    <AiOutlineCode size={20} />
                                </Box>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Navigation Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/pythonCodingDashBoard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            VIEW ALL STRUCTURES
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Scroll FAB */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    sx={{
                        position: "fixed",
                        bottom: 25,
                        right: 25,
                        bgcolor: "goldenrod",
                        color: "#100720",
                        "&:hover": { bgcolor: "#e0b200" }
                    }}
                >
                    <KeyboardArrowUpIcon />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
